import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api';
import { relativeTime } from '../lib/time';
import { Avatar, Spinner } from '@vatman/ui';
import './agentActivity.css';

/** One entry of the activity log (crates/server/src/activity.rs). */
export interface ActivityEntry {
  id: number;
  at: string;
  actorId: string | null;
  actorName: string | null;
  actorColor: string | null;
  action: string;
  targetKind: string;
  targetId: string | null;
  targetName: string | null;
  subjectId: string | null;
  subjectName: string | null;
  details: Record<string, unknown>;
}

interface Page {
  entries: ActivityEntry[];
  next: number | null;
}

/**
 * What people did in an organization or a team (owners and admins see it): one line per
 * action, newest first, with more on demand.
 */
export function ActivityLog({ path }: { path: string }) {
  const { t, i18n } = useTranslation();
  const log = useInfiniteQuery({
    queryKey: ['activity', path],
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) => api.get<Page>(`${path}?limit=50${pageParam ? `&before=${pageParam}` : ''}`),
    getNextPageParam: (last) => last.next,
  });
  const entries = log.data?.pages.flatMap((p) => p.entries) ?? [];
  if (log.isLoading) return <Spinner />;
  if (!entries.length) return <p className="muted">{t('activity.empty')}</p>;

  const role = (r: unknown) => (typeof r === 'string' && i18n.exists(`activity.role.${r}`) ? t(`activity.role.${r}`) : String(r ?? ''));
  const access = (a: unknown) => (typeof a === 'string' ? t(`access.${a}`).toLowerCase() : '');
  const line = (e: ActivityEntry) => {
    const d = e.details ?? {};
    const vars = {
      target: e.targetName ?? '',
      subject: e.subjectName ?? t('activity.someone'),
      role: role(d.role),
      link: access(d.link),
      team: access(d.team),
      org: access(d.org),
      key: String(d.key ?? ''),
      title: String(d.title ?? ''),
      system: String(d.system ?? ''),
      count: (Array.isArray(d.added) ? d.added.length : 0) + (Array.isArray(d.invited) ? d.invited.length : 0),
      state: d.enabled ? t('activity.on') : t('activity.off'),
    };
    const key = `activity.action.${e.action}`;
    return i18n.exists(key) ? t(key, vars) : `${e.action} ${vars.target}`;
  };

  return (
    <div className="activity-log">
      {entries.map((e) => (
        <div key={e.id} className="activity-row">
          <Avatar name={e.actorName ?? '?'} color={e.actorColor ?? '#8A8F98'} size={24} />
          <div className="activity-text">
            <b>{e.actorName ?? t('activity.someone')}</b> {line(e)}
            <div className="subtle activity-when">{relativeTime(Date.parse(e.at))}</div>
          </div>
        </div>
      ))}
      {log.hasNextPage && (
        <button className="btn sm" disabled={log.isFetchingNextPage} onClick={() => void log.fetchNextPage()}>
          {t('activity.more')}
        </button>
      )}
    </div>
  );
}
